/* eslint-disable react-refresh/only-export-components */
import { C } from '../lib/tokens'

export function Avatar({ initials, size = 36, gradient = 'linear-gradient(135deg, #00BF6D, #00A05C)' }) {
  return (
    <div className="kis-avatar" style={{
      width: size, height: size, borderRadius: size / 2, background: gradient,
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 760, fontSize: size * 0.34, letterSpacing: 0, flexShrink: 0,
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -10px 18px rgba(0,0,0,0.10), 0 10px 24px rgba(0,122,255,0.18)',
    }}>
      {initials}
    </div>
  )
}

export function Pill({ children, color = 'default' }) {
  const palette = {
    default: { bg: 'rgba(242,243,247,0.9)', fg: C.textSecondary, border: 'rgba(29,29,31,0.08)' },
    green:   { bg: 'rgba(227,249,237,0.95)', fg: C.greenDark, border: 'rgba(0,191,109,0.16)' },
    blue:    { bg: 'rgba(232,244,254,0.95)', fg: '#0057D9', border: 'rgba(0,122,255,0.16)' },
    orange:  { bg: 'rgba(255,243,232,0.96)', fg: '#A6520B', border: 'rgba(255,149,0,0.18)' },
    red:     { bg: 'rgba(253,234,234,0.96)', fg: '#A6201B', border: 'rgba(255,59,48,0.18)' },
    purple:  { bg: 'rgba(239,238,255,0.96)', fg: '#4338CA', border: 'rgba(99,91,255,0.16)' },
    yellow:  { bg: 'rgba(255,249,230,0.96)', fg: '#7A5A0B', border: 'rgba(245,158,11,0.18)' }
  }
  const c = palette[color] || palette.default
  return <span className={`kis-pill kis-pill-${color}`} style={{
    background: c.bg, color: c.fg, fontSize: 11.5, fontWeight: 760,
    padding: '3px 9px', borderRadius: 999, letterSpacing: 0, whiteSpace: 'nowrap',
    border: `1px solid ${c.border}`, lineHeight: 1.1, display: 'inline-flex',
    alignItems: 'center', minHeight: 22, boxSizing: 'border-box',
  }}>{children}</span>
}

export function Card({ children, style = {}, onClick, className }) {
  return (
    <div className={`kis-card${className ? ` ${className}` : ''}`} onClick={onClick} style={{
      position: 'relative',
      background: C.premiumSurface,
      borderRadius: 20,
      border: `1px solid ${C.border}`,
      boxShadow: C.shadowMedium,
      cursor: onClick ? 'pointer' : 'default',
      backdropFilter: 'blur(18px)',
      ...style
    }}>{children}</div>
  )
}

export function Btn({ children, onClick, variant = 'secondary', size = 'md', style = {}, disabled, type = 'button' }) {
  const sizes = {
    sm: { padding: '7px 14px', fontSize: 13 },
    md: { padding: '10px 18px', fontSize: 14 },
    lg: { padding: '12px 22px', fontSize: 15 }
  }
  const variants = {
    primary:   { background: 'linear-gradient(180deg, #2C3038 0%, #0E1117 100%)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 12px 26px rgba(17,24,39,0.22)' },
    secondary: { background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(247,250,253,0.90) 100%)', color: C.text, border: `1px solid ${C.borderStrong}`, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9), 0 10px 24px rgba(17,24,39,0.07)' },
    green:     { background: 'linear-gradient(180deg, #06C874 0%, #00A85F 100%)', color: '#fff', border: '1px solid rgba(255,255,255,0.18)', boxShadow: C.greenShadow },
    ghost:     { background: 'transparent', color: C.text, border: '1px solid transparent', boxShadow: 'none' }
  }
  return (
    <button className={`kis-btn kis-btn-${variant}`} type={type} onClick={onClick} disabled={disabled} style={{
      ...sizes[size], ...variants[variant],
      borderRadius: 999, fontWeight: 760,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
      letterSpacing: 0, transition: 'opacity 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease',
      display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: size === 'sm' ? 34 : size === 'lg' ? 46 : 40,
      fontFamily: 'inherit',
      ...style
    }}>{children}</button>
  )
}

export function SectionTitle({ children, style = {} }) {
  return <h3 style={{
    fontSize: 14, fontWeight: 820, color: C.text, letterSpacing: 0,
    margin: 0, marginBottom: 12, ...style
  }}>{children}</h3>
}

export const Icon = ({ d, size = 18, stroke = 'currentColor', fill = 'none', strokeWidth = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke}
       strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d={d} />
  </svg>
)

export const ICONS = {
  dashboard: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10',
  visits: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z',
  forms: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8',
  messages: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
  prescriptions: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  labs: 'M9 2v6L4 14a2 2 0 002 3h12a2 2 0 002-3l-5-6V2 M9 2h6 M8 14h8',
  orders: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293A1 1 0 005.414 17H17',
  bell: 'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 01-3.46 0',
  send: 'M22 2L11 13 M22 2l-7 20-4-9-9-4 20-7z',
  check: 'M20 6L9 17l-5-5',
  plus: 'M12 5v14 M5 12h14',
  signout: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4 M16 17l5-5-5-5 M21 12H9',
  paperclip: 'M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48',
  refill: 'M23 4v6h-6 M1 20v-6h6 M3.51 9a9 9 0 0114.85-3.36L23 10 M20.49 15a9 9 0 01-14.85 3.36L1 14',
  fileText: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8',
  pill: 'M10.5 20.5a4.95 4.95 0 01-7-7l9-9a4.95 4.95 0 017 7l-9 9zM8.5 8.5l7 7',
  spark: 'M12 3l1.9 5.8a2 2 0 001.3 1.3L21 12l-5.8 1.9a2 2 0 00-1.3 1.3L12 21l-1.9-5.8a2 2 0 00-1.3-1.3L3 12l5.8-1.9a2 2 0 001.3-1.3L12 3z',
  video: 'M23 7l-7 5 7 5V7z M14 5H3a2 2 0 00-2 2v10a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2z'
}

export function Logo({ size = 32, withWordmark = false }) {
  return (
    <div className="kis-logo-lockup" style={{ display: 'flex', alignItems: 'center', gap: withWordmark ? 0 : 10 }}>
      {withWordmark ? (
        <div style={{ fontSize: 16, fontWeight: 820, color: C.text, letterSpacing: 0 }}>
          KIS <span style={{ color: C.green }}>Rx</span>
        </div>
      ) : (
        <img src="/logo.png" alt="KIS Rx" style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          flexShrink: 0,
          background: '#fff',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.7), 0 2px 4px rgba(17,24,39,0.08), 0 14px 30px rgba(17,24,39,0.10)',
        }} />
      )}
    </div>
  )
}
